import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Dropdown, Space, Badge, Alert } from 'antd';
import {
  DashboardOutlined,
  AlertOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  TabletOutlined,
  ExperimentOutlined,
  MenuOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import apiError from '../../utils/apiError';
import './index.css';

const { Header, Sider, Content } = Layout;

const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(window.innerWidth <= 768);
  const [userInfo, setUserInfo] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [globalError, setGlobalError] = useState('');

  // 订阅全局 API 错误
  useEffect(() => {
    const off = apiError.on((msg) => {
      setGlobalError(msg);
    });
    return off;
  }, []);

  // 路由变化时清除错误
  useEffect(() => {
    setGlobalError('');
  }, [location.pathname]);

  useEffect(() => {
    const info = localStorage.getItem('user');
    if (info) {
      setUserInfo(JSON.parse(info));
    }
  }, []);

  // 监听屏幕尺寸变化
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile && !collapsed) {
        setCollapsed(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [collapsed]);

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: '数据仪表盘',
      roles: ['admin', 'user'], // 所有角色可见
    },
    {
      key: '/devices',
      icon: <TabletOutlined />,
      label: '设备管理',
      roles: ['admin', 'user'], // 所有角色可见
    },
    {
      key: '/users',
      icon: <UserOutlined />,
      label: '用户管理',
      roles: ['admin'], // 仅管理员可见
    },
    {
      key: '/simulates',
      icon: <ExperimentOutlined />,
      label: '地震演习',
      roles: ['admin', 'user'], // 所有角色可见
    },
  ];

  // 根据用户角色过滤菜单
  const filteredMenuItems = menuItems.filter(item => {
    if (!userInfo || !userInfo.role) {
      return false; // 如果没有用户信息，不显示任何菜单
    }
    return item.roles.includes(userInfo.role);
  });

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleUserMenuClick = ({ key }) => {
    if (key === 'logout') {
      handleLogout();
    } else if (key === 'profile') {
      navigate('/profile');
    }
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人信息',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  const handleMenuClick = ({ key }) => {
    navigate(key);
    // 移动端点击菜单后自动收起侧边栏
    if (isMobile) {
      setCollapsed(true);
    }
  };

  return (
    <Layout className="main-layout">
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        className="main-sider"
        width={isMobile ? 200 : 240}
        collapsedWidth={isMobile ? 0 : 80}
        breakpoint="md"
        trigger={isMobile ? null : undefined}
      >
        <div className="logo">
          <AlertOutlined className="logo-icon" />
          {!collapsed && <span className="logo-text">rhino 终端服务</span>}
        </div>
        <Menu
          theme="dark"
          selectedKeys={[location.pathname]}
          mode="inline"
          items={filteredMenuItems}
          onClick={handleMenuClick}
        />
      </Sider>
      <Layout>
        <Header className="main-header">
          <div className="header-left">
            {isMobile && (
              <MenuOutlined
                className="menu-trigger"
                onClick={() => setCollapsed(!collapsed)}
              />
            )}
          </div>
          <div className="header-right">
            <Space size="large">
              <Badge count={0} showZero={false}>
                <Avatar
                  style={{ backgroundColor: '#87d068' }}
                  icon={<UserOutlined />}
                />
              </Badge>
              <Dropdown
                menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
                placement="bottomRight"
              >
                <Space className="user-info">
                  <span className="username">{userInfo?.username || '用户'}</span>
                  <span className="enterprise">{userInfo?.enterprise || ''}</span>
                </Space>
              </Dropdown>
            </Space>
          </div>
        </Header>
        <Content className="main-content">
          <div className="content-wrapper">
            {/* 全局错误提示 - 预留固定高度防止布局跳动 */}
            <div style={{ minHeight: 42, marginBottom: 16 }}>
              {globalError && (
                <Alert
                  message={globalError}
                  type="error"
                  showIcon
                  closable
                  onClose={() => setGlobalError('')}
                />
              )}
            </div>
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
